import { DOCUMENT } from '@angular/common';
import { Component, NgZone, OnInit, inject } from '@angular/core';
import { OktaAuthStateService } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import {
  Observable,
  combineLatest,
  filter,
  map,
  merge,
  startWith,
  take,
} from 'rxjs';
import { UserInfoService } from './services/userinfo/userinfo.service';
import { plainToInstance } from 'class-transformer';
import { AccessRightsDto, OktaJwtDto } from '@wex-risk/rp-portfolio-utils';
import { EnvironmentService } from './services/environment.service';
import { faPlusMinus } from '@fortawesome/free-solid-svg-icons';
import { Router } from '@angular/router';
import { RAGApiService } from './services/rag-api/rag-api.service';
import { PAGE_ICONS, alwaysVisiblePaths } from './global-constants';
import { BusinessProfileFeatureFlagService } from './services/feature-flags/business-profile-feature-flag.service';
import { NavigationItemViewModel } from './models/navigation-item-view.model';
import { FinancialAnalysisFeatureState } from './services/feature-flags/models/financial-analysis-feature-state.model';
import { ThemeService } from './services/theme/theme.service';

type EnvironmentBadge = {
  label: string;
  tone: 'local' | 'dev' | 'stage';
};

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
// main app component
export class AppComponent implements OnInit {
  private readonly oktaStateService = inject(OktaAuthStateService);
  private readonly oktaAuth = inject(OktaAuth);
  private readonly userInfoService = inject(UserInfoService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly businessProfileFeatureFlagService = inject(
    BusinessProfileFeatureFlagService,
  );
  private readonly ragApiService = inject(RAGApiService);
  private readonly router = inject(Router);
  private readonly themeService = inject(ThemeService);
  private readonly document = inject(DOCUMENT);
  private readonly ngZone = inject(NgZone);

  private static readonly NAV_COLLAPSED_STORAGE_KEY = 'nav-collapsed';

  // Caps how long the header/nav skeleton waits on `document.fonts.load()`.
  // That promise settles once a font's request fails or decodes, but a
  // stalled/never-responding request wouldn't otherwise settle at all.
  private static readonly FONT_READY_TIMEOUT_MS = 3000;

  // `hasAccess()` treats admin settings still being `null` as "not loaded"
  // and hides every link except Home until they arrive. On a failed fetch
  // they stay `null` forever (see RAGApiService.syncAdminSettingsState), so
  // this timeout keeps that failure from hiding the nav skeleton forever
  // too, once it's included in `isNavLoading$` below.
  private static readonly ADMIN_SETTINGS_READY_TIMEOUT_MS = 5000;

  protected readonly icon = faPlusMinus;
  protected readonly title = 'WEX Risk Action Gateway';
  protected readonly environmentBadge = this.createEnvironmentBadge(
    this.environmentService.environmentName,
  );

  private readonly baseLinks: NavigationItemViewModel[] = [
    {
      url: '/home',
      icon: PAGE_ICONS.home,
      name: 'Home',
      isVisible: true,
    },
    {
      url: '/submit',
      icon: PAGE_ICONS.submit,
      name: 'Bulk Account Actions',
      isVisible: true,
    },
    {
      url: '/exclusions',
      icon: PAGE_ICONS.exclusions,
      name: 'Account Exclusions',
      isVisible: true,
    },
    {
      url: '/r360',
      icon: PAGE_ICONS.r360,
      name: 'R360',
      isVisible: true,
    },
    {
      url: '/processedfiles',
      icon: PAGE_ICONS.processedFiles,
      name: 'Submitted Files',
      isVisible: true,
    },
    {
      url: '/manualreview',
      icon: PAGE_ICONS.manualReview,
      name: 'Credit Line Requests',
      isVisible: true,
    },
    {
      url: '/financial-analysis',
      icon: PAGE_ICONS.financialAnalysis,
      name: 'Financial Analysis',
      isVisible: false,
    },
    {
      url: '/settings',
      icon: PAGE_ICONS.settings,
      name: 'Admin Settings',
      isVisible: true,
    },
  ];

  protected readonly links$: Observable<NavigationItemViewModel[]>;
  protected readonly isNavLoading$: Observable<boolean>;

  protected user: OktaJwtDto | undefined;
  protected userDisplayName = 'User';
  protected userEmail = '';
  protected userInitial = 'U';
  protected isLoading = true;
  protected areShellFontsReady = false;
  protected isNavCollapsed = false;

  constructor() {
    const oktaAuth = this.oktaAuth;

    const storedNavCollapsed = localStorage.getItem(
      AppComponent.NAV_COLLAPSED_STORAGE_KEY,
    );
    // Without a saved preference, start icon-only on narrow viewports so the
    // content area isn't squeezed by the full-width nav (matches the
    // `width <= 700px` breakpoint already used elsewhere in this shell).
    this.isNavCollapsed =
      storedNavCollapsed !== null
        ? storedNavCollapsed === 'true'
        : (this.document.defaultView?.innerWidth ?? Infinity) <= 700;

    this.links$ = this.businessProfileFeatureFlagService.featureState$.pipe(
      map((featureState: FinancialAnalysisFeatureState) =>
        this.baseLinks.map((link: NavigationItemViewModel) =>
          link.url === '/financial-analysis'
            ? {
                ...link,
                isVisible: featureState.isEnabled,
              }
            : link,
        ),
      ),
    );

    this.isNavLoading$ = combineLatest([
      this.businessProfileFeatureFlagService.featureState$.pipe(
        map(() => false),
        startWith(true),
      ),
      merge(
        this.ragApiService.adminSettings$.pipe(
          filter((settings) => settings !== null),
          map(() => true),
        ),
        this.createOutsideZoneTimeout(
          AppComponent.ADMIN_SETTINGS_READY_TIMEOUT_MS,
        ),
      ).pipe(take(1), startWith(false)),
    ]).pipe(
      map(
        ([isFeatureFlagLoading, isAdminSettingsReady]) =>
          isFeatureFlagLoading || !isAdminSettingsReady,
      ),
    );

    oktaAuth.tokenManager.on('expired', () => {
      console.log('expired');
      this.signOut().catch(console.error);
    });
  }

  //retrieves user's information and passes it to userInfoService
  public ngOnInit(): void {
    this.themeService.init();

    const fonts = this.document.fonts;
    if (fonts) {
      const fontsReady = Promise.all([
        fonts.load('400 16px Inter'),
        fonts.load('600 16px Inter'),
      ]).catch(() => []);
      const timeout = new Promise<void>((resolve) => {
        setTimeout(resolve, AppComponent.FONT_READY_TIMEOUT_MS);
      });

      void Promise.race([fontsReady, timeout]).then(() => {
        this.areShellFontsReady = true;
      });
    } else {
      this.areShellFontsReady = true;
    }

    // Initialize feature flag immediately if ignoreScopes is enabled
    if (this.environmentService.ignoreScopes) {
      this.businessProfileFeatureFlagService.initialize();
      this.isLoading = false;
    }

    this.oktaStateService.authState$
      .pipe(filter((authState) => authState?.isAuthenticated === true))
      .subscribe(() => {
        // Initialize feature flag after authentication is confirmed
        this.businessProfileFeatureFlagService.initialize();

        this.oktaAuth
          .getUser()
          .then((claims) => {
            this.user = plainToInstance(OktaJwtDto, claims);
            this.userDisplayName = this.user.name?.trim() || 'User';
            this.userEmail = claims.email?.trim() ?? '';
            this.userInitial = this.userDisplayName.charAt(0).toUpperCase();
            this.userInfoService.setUser(this.user);
            this.isLoading = false;
          })
          .catch(console.error);
      });
  }
  /**
   * This method checks if the current user has access to a given path.
   *
   * @param {string} path - The path to check access for.
   * @returns {boolean} - Returns true if the user has access, false otherwise.
   */
  public hasAccess(path: string): boolean {
    const accessRights: AccessRightsDto[] = [];

    let isProcessFilesDisabled: boolean | null = null;
    this.ragApiService.adminSettings$.subscribe((settings) => {
      isProcessFilesDisabled = settings?.enabled ?? null;
    });

    const alwaysVisible: string[] = alwaysVisiblePaths;
    const isPathAlwaysVisible = alwaysVisible.includes(path);

    if (isPathAlwaysVisible) {
      return true;
    }

    if (isProcessFilesDisabled === null) {
      return false;
    }

    if (isProcessFilesDisabled && path !== '/settings') {
      return false;
    }

    switch (path) {
      case '/submit':
        accessRights.push(AccessRightsDto.UPDATE_ACCOUNT_CREDITLINE);
        break;
      case '/processedfiles':
        accessRights.push(
          AccessRightsDto.ACCESS_ONLY_MY_SUBMITTED_FILES,
          AccessRightsDto.ACCESS_OTHERS_SUBMITTED_FILES,
        );
        break; // this is done more granularly
      case '/home':
        return true; // everyone can see the home page
      case '/exclusions':
        accessRights.push(
          AccessRightsDto.ACCESS_NAF_ACCOUNTS,
          AccessRightsDto.ACCESS_OTR_ACCOUNTS,
          AccessRightsDto.UPDATE_ACCOUNT_CREDITLINE,
          AccessRightsDto.UPDATE_ACCOUNT_STATUS_SUSPEND,
          AccessRightsDto.ACCESS_OTHERS_SUBMITTED_FILES,
        );
        break;
      case '/manualreview':
        accessRights.push(AccessRightsDto.UPDATE_ACCOUNT_MANUAL_REVIEW);
        break;
      case '/r360':
        accessRights.push(AccessRightsDto.ACCESS_R360_VIEW_ONLY);
        break;
      case '/financial-analysis':
        return true;
      case '/settings':
        accessRights.push(AccessRightsDto.ACCESS_ADMIN_SETTINGS);
        break;
      default:
        console.warn(`No access rights defined for path: ${path}`);
        return false;
    }

    if (accessRights.length === 0) {
      return true;
    }
    const hasAccessRights = this.user?.hasAnyAccessRights(...accessRights);
    return hasAccessRights || this.environmentService.ignoreScopes;
  }

  /**
   * Toggles the left-hand navigation between its full and icon-only widths,
   * persisting the choice so it survives a page reload. Blurs the triggering
   * button afterward so its focus ring doesn't linger once collapsed.
   */
  public toggleNav(event: MouseEvent): void {
    this.isNavCollapsed = !this.isNavCollapsed;
    localStorage.setItem(
      AppComponent.NAV_COLLAPSED_STORAGE_KEY,
      String(this.isNavCollapsed),
    );
    (event.currentTarget as HTMLElement | null)?.blur();
  }

  //signs out the user
  public async signOut(): Promise<void> {
    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();

    // Clear all cookies
    document.cookie.split(';').forEach((c) => {
      document.cookie = c
        .replace(/^ +/, '')
        .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
    });

    // Clear Okta storage
    this.oktaAuth.clearStorage();

    // Sign out with redirect to login
    await this.oktaAuth.signOut({
      postLogoutRedirectUri: window.location.origin,
    });
  }

  private createEnvironmentBadge(
    environmentName: string,
  ): EnvironmentBadge | null {
    switch (environmentName.trim().toLowerCase()) {
      case 'production':
      case 'prod':
        return null;
      case 'local':
        return { label: 'LOCAL', tone: 'local' };
      case 'stage':
        return { label: 'STAGE', tone: 'stage' };
      case 'development':
      case 'dev':
        return { label: 'DEVELOPMENT', tone: 'dev' };
      default:
        return { label: environmentName.toUpperCase(), tone: 'dev' };
    }
  }

  /**
   * A one-shot timer whose `setTimeout` runs outside Angular's zone, so it
   * doesn't count as a pending task NgZone/`whenStable()` wait on for its
   * full duration (as `rxjs`'s `timer()` would, since zone.js patches it).
   * Re-enters the zone only once it actually fires, to trigger change
   * detection for subscribers like the `| async` pipe in the template.
   */
  private createOutsideZoneTimeout(ms: number): Observable<true> {
    return new Observable<true>((subscriber) => {
      const timeoutId = this.ngZone.runOutsideAngular(() =>
        setTimeout(() => {
          this.ngZone.run(() => {
            subscriber.next(true);
            subscriber.complete();
          });
        }, ms),
      );
      return () => clearTimeout(timeoutId);
    });
  }
}
